/**
 * /me — extended user profile.
 *
 * Two panels:
 *   1. My profile — public + private fields user can edit at any time.
 *   2. Verification — status banner + request form.
 *
 * Verified users' submissions (events, memorials, causes) auto-approve.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import SignInMethodsTab from "./SignInMethodsTab";
import {
  UserCircle2, Camera, Save, ShieldCheck, Clock3, ShieldAlert, Sparkles,
  Linkedin, Twitter, Instagram, Facebook, Globe, MapPin, Phone, Mail,
  Building2, Plus, ExternalLink,
} from "lucide-react";
import { useMyBusinesses } from "@/lib/useMyBusinesses";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const STATUS_STYLES = {
  unverified: { label: "Not verified", cls: "bg-neutral-100 text-neutral-700", icon: ShieldAlert },
  pending: { label: "Under review", cls: "bg-amber-100 text-amber-800", icon: Clock3 },
  verified: { label: "Verified contributor", cls: "bg-emerald-100 text-emerald-800", icon: ShieldCheck },
  rejected: { label: "Verification declined", cls: "bg-red-100 text-red-800", icon: ShieldAlert },
};

export default function MyProfile() {
  const { user: authUser, refresh } = useAuth();
  const [params] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(() => {
    const t = params.get("tab");
    if (params.get("link-email") === "1") return "signin";
    return ["profile", "verification", "family", "signin"].includes(t) ? t : "profile";
  }); // profile | verification | family | signin

  const load = async () => {
    if (!authUser) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await http.get("/me/profile");
      setProfile(data.user);
    } catch (e) {
      toast.error("Failed to load profile");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [authUser?.id]);

  if (!authUser && !loading) {
    return (
      <div className="max-w-[900px] mx-auto p-16 text-center">
        <UserCircle2 className="w-12 h-12 mx-auto opacity-40 mb-3" />
        <h1 className="font-display text-3xl">Please sign in to see your profile.</h1>
        <Link to="/login?next=/me" className="wofy-btn-primary mt-6 inline-flex">Sign in</Link>
      </div>
    );
  }

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Profile picture must be under 500 KB — please compress first");
    try {
      const data = await fileToDataUrl(file);
      update({ profile_picture: data });
      toast.success("Photo ready — click Save changes to apply");
    } catch { toast.error("Could not read file"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        name: profile.name,
        bio: profile.bio,
        city: profile.city,
        state: profile.state,
        country: profile.country,
        postal_code: profile.postal_code,
        street_address: profile.street_address,
        phone: profile.phone,
        profile_picture: profile.profile_picture,
        website: profile.website,
        linkedin_url: profile.linkedin_url,
        twitter_url: profile.twitter_url,
        instagram_url: profile.instagram_url,
        facebook_url: profile.facebook_url,
        is_volunteer: profile.is_volunteer || false,
      };
      const { data } = await http.patch("/me/profile", patch);
      setProfile(data.user);
      toast.success("Profile updated");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading || !profile) {
    return <div className="max-w-[900px] mx-auto p-16 text-center opacity-70">Loading your profile…</div>;
  }

  const statusKey = profile.is_admin ? "verified" : (profile.verification_status || "unverified");
  const StatusBadge = STATUS_STYLES[statusKey] || STATUS_STYLES.unverified;
  const Icon = StatusBadge.icon;

  return (
    <div data-testid="my-profile-page" className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-start gap-5">
          <div className="relative">
            {profile.profile_picture ? (
              <img
                src={profile.profile_picture}
                alt={profile.name}
                className="w-24 h-24 rounded-full object-cover border-4"
                style={{ borderColor: "var(--wofy-primary)" }}
                data-testid="profile-picture"
              />
            ) : (
              <div
                className="w-24 h-24 rounded-full grid place-items-center border-4"
                style={{ borderColor: "var(--wofy-primary)", background: "var(--wofy-surface)" }}
              >
                <UserCircle2 className="w-12 h-12 opacity-50" />
              </div>
            )}
            <label
              htmlFor="pf-photo"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full grid place-items-center cursor-pointer border-2"
              style={{ background: "var(--wofy-primary)", color: "#FDFBF7", borderColor: "var(--wofy-bg)" }}
              title="Change photo"
            >
              <Camera className="w-4 h-4" />
            </label>
            <input id="pf-photo" data-testid="pf-photo-upload" type="file" accept="image/*" className="hidden" onChange={upload} />
          </div>
          <div>
            <div className="overline mb-1">MY PROFILE</div>
            <h1 className="font-display text-3xl md:text-4xl leading-tight">{profile.name}</h1>
            <div className="mt-2 text-sm opacity-70 flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {profile.email}</span>
              {[profile.city, profile.country].filter(Boolean).length > 0 && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {[profile.city, profile.country].filter(Boolean).join(", ")}
                </span>
              )}
            </div>
            <div className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${StatusBadge.cls}`} data-testid="verification-badge">
              <Icon className="w-3.5 h-3.5" /> {StatusBadge.label}
            </div>
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-profile-btn" className="wofy-btn-primary">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1.5">
        {[
          { v: "profile", label: "Profile" },
          { v: "verification", label: `Verification ${statusKey === "verified" ? "✓" : ""}` },
          { v: "family", label: "Family Digest" },
          { v: "signin", label: "Sign-in methods" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            data-testid={`tab-${t.v}`}
            className={`px-4 py-2 text-sm rounded-full ${tab === t.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={tab === t.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <>
          <MyBusinessesCard />
          <MyImportsCard />
          <ProfileTab profile={profile} update={update} />
        </>
      )}
      {tab === "verification" && (
        <VerificationTab profile={profile} reload={load} />
      )}
      {tab === "family" && (
        <FamilyDigestTab />
      )}
      {tab === "signin" && (
        <SignInMethodsTab />
      )}
    </div>
  );
}

function MyImportsCard() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/me/content-imports");
        setItems(data.items || []);
      } catch { setItems([]); }
    })();
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <section
      className="mt-4 rounded-xl p-5 md:p-6 border"
      style={{ borderColor: "var(--wofy-border)", background: "#FFF7ED" }}
      data-testid="profile-my-imports"
    >
      <div className="overline mb-1">CONTENT IMPORT AUTHORIZATIONS</div>
      <h2 className="font-display text-xl mb-1">My import consents</h2>
      <p className="text-xs opacity-70 mb-3">Every time you import content from an external website into a business page, we save the authorization you signed. This is your audit trail.</p>
      <ul className="text-xs divide-y" style={{ borderColor: "var(--wofy-border)" }}>
        {items.map((it) => (
          <li key={it.id} className="py-2 flex flex-wrap items-center gap-2" data-testid={`import-consent-${it.id}`}>
            <span className="font-mono opacity-70 text-[10px]">{new Date(it.created_at).toLocaleString()}</span>
            <span className="opacity-90 truncate flex-1">{it.url}</span>
            <span className="text-[10px] opacity-60 font-mono">signed by {it.consent_signature || "—"} · v{it.consent_version}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MyBusinessesCard() {
  const { items, loading } = useMyBusinesses();
  if (loading) return null;
  const hasBiz = items.length > 0;
  return (
    <section
      className="mt-8 rounded-xl p-5 md:p-6 border"
      style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, rgba(178,93,56,0.08), rgba(178,93,56,0.02))" }}
      data-testid="profile-my-businesses"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="overline mb-1">MY BUSINESSES</div>
          <h2 className="font-display text-2xl">
            {hasBiz ? "Manage your business pages" : "Own a business?"}
          </h2>
          <p className="text-sm opacity-70 mt-1 max-w-xl">
            {hasBiz
              ? "Every business here has its own public profile — edit info, invite team members, and run your storefront."
              : "Get a free public business page in minutes. Add a shopping cart later when you're ready to sell."}
          </p>
        </div>
        <Link
          to="/businesses/new"
          className="wofy-btn-primary inline-flex items-center gap-1.5"
          data-testid="add-business-cta"
        >
          <Plus className="w-4 h-4" /> Add a business
        </Link>
      </div>
      {hasBiz && (
        <ul className="mt-4 grid gap-2 md:grid-cols-2" data-testid="profile-business-list">
          {items.map((biz) => (
            <li key={biz.id} className="rounded-lg border bg-white p-3 flex items-center gap-3" style={{ borderColor: "var(--wofy-border)" }} data-testid={`profile-biz-${biz.slug}`}>
              <div className="w-10 h-10 rounded-lg shrink-0 grid place-items-center text-white text-lg font-display" style={{ background: biz.logo_url ? undefined : "var(--wofy-primary)" }}>
                {biz.logo_url
                  ? <img src={biz.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  : (biz.name || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 opacity-60" /> {biz.name}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">
                  {biz._role} · {biz._plan?.name || biz.plan || "free"}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Link to={`/business/${biz.slug}`} className="text-[11px] inline-flex items-center gap-1 opacity-80 hover:opacity-100" data-testid={`profile-biz-view-${biz.slug}`}>
                  View <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to={`/business/${biz.slug}/edit`} className="text-[11px] inline-flex items-center gap-1 font-medium" style={{ color: "var(--wofy-primary)" }} data-testid={`profile-biz-edit-${biz.slug}`}>
                  Edit →
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Field({ label, children, private_ }) {  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider opacity-70 flex items-center gap-1.5">
        {label}
        {private_ && <span className="text-[9px] font-medium rounded px-1.5 py-0.5 bg-neutral-200 text-neutral-600">PRIVATE</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ProfileTab({ profile, update }) {
  const inp = "w-full px-3 py-2 rounded-lg border bg-transparent text-sm";
  const st = { borderColor: "var(--wofy-border)" };
  return (
    <div className="mt-8 grid gap-6 md:grid-cols-2">
      <section className="wofy-card space-y-4">
        <div className="overline">BASICS</div>
        <Field label="Full name">
          <input data-testid="field-name" value={profile.name || ""} onChange={(e) => update({ name: e.target.value })} className={inp} style={st} />
        </Field>
        <Field label="Bio">
          <textarea data-testid="field-bio" rows={4} value={profile.bio || ""} onChange={(e) => update({ bio: e.target.value })} maxLength={1000} className={inp} style={st} placeholder="A few sentences about yourself…" />
        </Field>
        <Field label="Are you a volunteer?">
          <label className="inline-flex items-center gap-2 text-sm">
            <input data-testid="field-volunteer" type="checkbox" checked={!!profile.is_volunteer} onChange={(e) => update({ is_volunteer: e.target.checked })} />
            Yes — I'm available to volunteer
          </label>
        </Field>
      </section>

      <section className="wofy-card space-y-4">
        <div className="overline">CONTACT</div>
        <Field label="Phone" private_>
          <input data-testid="field-phone" value={profile.phone || ""} onChange={(e) => update({ phone: e.target.value })} className={inp} style={st} placeholder="+1 555 123 4567" />
        </Field>
        <Field label="Email">
          <input value={profile.email || ""} disabled className={`${inp} opacity-60`} style={st} />
        </Field>
      </section>

      <section className="wofy-card space-y-4">
        <div className="overline">LOCATION</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City"><input data-testid="field-city" value={profile.city || ""} onChange={(e) => update({ city: e.target.value })} className={inp} style={st} /></Field>
          <Field label="State / Province"><input data-testid="field-state" value={profile.state || ""} onChange={(e) => update({ state: e.target.value })} className={inp} style={st} /></Field>
          <Field label="Country"><input data-testid="field-country" value={profile.country || ""} onChange={(e) => update({ country: e.target.value })} className={inp} style={st} /></Field>
          <Field label="Postal code" private_><input value={profile.postal_code || ""} onChange={(e) => update({ postal_code: e.target.value })} className={inp} style={st} /></Field>
        </div>
        <Field label="Full street address" private_>
          <input data-testid="field-address" value={profile.street_address || ""} onChange={(e) => update({ street_address: e.target.value })} className={inp} style={st} placeholder="Number, street, apartment…" />
        </Field>
        <p className="text-[11px] opacity-60">Private fields are visible only to you and platform administrators.</p>
      </section>

      <section className="wofy-card space-y-4">
        <div className="overline">SOCIAL</div>
        <Field label="Website"><div className="flex items-center gap-2"><Globe className="w-4 h-4 opacity-60" /><input data-testid="field-website" value={profile.website || ""} onChange={(e) => update({ website: e.target.value })} className={inp} style={st} placeholder="https://" /></div></Field>
        <Field label="LinkedIn"><div className="flex items-center gap-2"><Linkedin className="w-4 h-4 opacity-60" /><input data-testid="field-linkedin" value={profile.linkedin_url || ""} onChange={(e) => update({ linkedin_url: e.target.value })} className={inp} style={st} placeholder="https://linkedin.com/in/…" /></div></Field>
        <Field label="X / Twitter"><div className="flex items-center gap-2"><Twitter className="w-4 h-4 opacity-60" /><input value={profile.twitter_url || ""} onChange={(e) => update({ twitter_url: e.target.value })} className={inp} style={st} placeholder="https://x.com/…" /></div></Field>
        <Field label="Instagram"><div className="flex items-center gap-2"><Instagram className="w-4 h-4 opacity-60" /><input value={profile.instagram_url || ""} onChange={(e) => update({ instagram_url: e.target.value })} className={inp} style={st} placeholder="https://instagram.com/…" /></div></Field>
        <Field label="Facebook"><div className="flex items-center gap-2"><Facebook className="w-4 h-4 opacity-60" /><input value={profile.facebook_url || ""} onChange={(e) => update({ facebook_url: e.target.value })} className={inp} style={st} placeholder="https://facebook.com/…" /></div></Field>
      </section>
    </div>
  );
}

function VerificationTab({ profile, reload }) {
  const status = profile.is_admin ? "verified" : (profile.verification_status || "unverified");
  const [form, setForm] = useState(() => ({
    full_name: profile.name || "",
    phone: profile.phone || "",
    email: profile.email || "",
    country: profile.country || "",
    state: profile.state || "",
    city: profile.city || "",
    street_address: profile.street_address || "",
    postal_code: profile.postal_code || "",
    linkedin_url: profile.linkedin_url || "",
    organization: "",
    is_volunteer: !!profile.is_volunteer,
    volunteer_causes: (profile.volunteer_causes || []).join(", "),
    id_document_type: "",
    id_document_note: "",
    references: "",
    consent: false,
  }));
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const inp = "w-full px-3 py-2 rounded-lg border bg-transparent text-sm";
  const st = { borderColor: "var(--wofy-border)" };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please confirm the information is accurate");
    setBusy(true);
    try {
      const body = {
        ...form,
        volunteer_causes: form.volunteer_causes
          ? form.volunteer_causes.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      await http.post("/me/verification-request", body);
      toast.success("Verification request submitted — we'll email you when it's reviewed.");
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  if (status === "verified") {
    return (
      <div className="mt-8 wofy-card text-center py-10" data-testid="verified-message">
        <ShieldCheck className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
        <h2 className="font-display text-2xl">
          {profile.is_admin ? "You're an admin — trusted contributor." : "You are a verified contributor."}
        </h2>
        <p className="mt-2 text-sm max-w-xl mx-auto opacity-80">
          Events, memorials and causes you submit will publish immediately —
          no editor review needed. Thank you for being part of Social Pulse.
        </p>
        {profile.verification_reviewed_at && !profile.is_admin && (
          <div className="mt-4 text-[11px] font-mono opacity-60">
            Approved {profile.verification_reviewed_at.slice(0, 10)}
            {profile.verification_reviewed_by ? ` by ${profile.verification_reviewed_by}` : ""}
          </div>
        )}
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="mt-8 wofy-card" data-testid="verification-pending">
        <div className="flex items-start gap-3">
          <Clock3 className="w-6 h-6 text-amber-600 shrink-0 mt-1" />
          <div>
            <h2 className="font-display text-2xl">Your verification is under review.</h2>
            <p className="mt-2 text-sm opacity-80 max-w-xl">
              Our team reviews requests within 2–3 business days. Meanwhile,
              feel free to browse and submit content — your submissions will
              go through the standard editor queue until you're verified.
            </p>
            {profile.verification_submitted_at && (
              <div className="mt-3 text-[11px] font-mono opacity-60">
                Submitted {profile.verification_submitted_at.slice(0, 10)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="verification-form" className="mt-8 space-y-6">
      <div className="wofy-card">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 shrink-0 mt-1" style={{ color: "var(--wofy-primary)" }} />
          <div>
            <h2 className="font-display text-2xl">Request contributor verification</h2>
            <p className="mt-2 text-sm opacity-80 max-w-2xl">
              Verified contributors can publish events, causes and memorials
              instantly — no editor queue. We use the info below to confirm
              you're a real person or a genuine community organizer.
              All details stay private to platform admins.
              {status === "rejected" && (
                <span className="block mt-2 text-red-700 font-medium">
                  Your previous request was declined
                  {profile.verification_review_comment ? `: "${profile.verification_review_comment}"` : "."}
                  {" "}Please update your info and resubmit.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="wofy-card grid gap-4 md:grid-cols-2">
        <Field label="Full legal name *"><input data-testid="vr-name" required value={form.full_name} onChange={(e) => upd("full_name", e.target.value)} className={inp} style={st} /></Field>
        <Field label="Phone *"><input data-testid="vr-phone" required value={form.phone} onChange={(e) => upd("phone", e.target.value)} className={inp} style={st} placeholder="+1 555 123 4567" /></Field>
        <Field label="Email *"><input required type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} className={inp} style={st} /></Field>
        <Field label="LinkedIn URL"><input data-testid="vr-linkedin" value={form.linkedin_url} onChange={(e) => upd("linkedin_url", e.target.value)} className={inp} style={st} placeholder="https://linkedin.com/in/…" /></Field>
        <Field label="Country *"><input data-testid="vr-country" required value={form.country} onChange={(e) => upd("country", e.target.value)} className={inp} style={st} /></Field>
        <Field label="State / Province"><input value={form.state} onChange={(e) => upd("state", e.target.value)} className={inp} style={st} /></Field>
        <Field label="City *"><input data-testid="vr-city" required value={form.city} onChange={(e) => upd("city", e.target.value)} className={inp} style={st} /></Field>
        <Field label="Postal code"><input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} className={inp} style={st} /></Field>
        <div className="md:col-span-2">
          <Field label="Full street address *"><input data-testid="vr-address" required value={form.street_address} onChange={(e) => upd("street_address", e.target.value)} className={inp} style={st} placeholder="Number, street, apartment…" /></Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Organization (if applicable)"><input value={form.organization} onChange={(e) => upd("organization", e.target.value)} className={inp} style={st} placeholder="Charity, church, community group…" /></Field>
        </div>
      </div>

      <div className="wofy-card space-y-4">
        <div className="overline">VOLUNTEER</div>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="vr-volunteer" type="checkbox" checked={form.is_volunteer} onChange={(e) => upd("is_volunteer", e.target.checked)} />
          I'm available to volunteer time
        </label>
        {form.is_volunteer && (
          <Field label="Causes you'd volunteer for (comma-separated)">
            <input value={form.volunteer_causes} onChange={(e) => upd("volunteer_causes", e.target.value)} className={inp} style={st} placeholder="housing, food security, education…" />
          </Field>
        )}
      </div>

      <div className="wofy-card space-y-4">
        <div className="overline">IDENTITY (OPTIONAL)</div>
        <p className="text-xs opacity-70">
          If we have questions we'll email you. You can also volunteer a note about how we can verify you, e.g. "I run a Meetup group, here's the link…"
        </p>
        <Field label="ID document type (e.g. passport, driver's license)"><input value={form.id_document_type} onChange={(e) => upd("id_document_type", e.target.value)} className={inp} style={st} /></Field>
        <Field label="Additional note"><textarea rows={3} value={form.id_document_note} onChange={(e) => upd("id_document_note", e.target.value)} maxLength={1000} className={inp} style={st} /></Field>
        <Field label="References (people who can vouch for you)"><textarea rows={3} value={form.references} onChange={(e) => upd("references", e.target.value)} maxLength={1000} className={inp} style={st} /></Field>
      </div>

      <div className="wofy-card">
        <label className="flex items-start gap-3 text-sm">
          <input data-testid="vr-consent" required type="checkbox" checked={form.consent} onChange={(e) => upd("consent", e.target.checked)} className="mt-1" />
          <span>
            I confirm the information above is accurate. I understand Social Pulse
            keeps my private details confidential and uses them only to verify
            my identity for the purpose of trusted contributor status.
          </span>
        </label>
      </div>

      <div className="flex justify-end">
        <button disabled={busy} data-testid="vr-submit" className="wofy-btn-primary">
          {busy ? "Submitting…" : "Submit for verification"}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Family Anniversary Digest tab                                      */
/* ------------------------------------------------------------------ */
function FamilyDigestTab() {
  const [prefs, setPrefs] = useState({ enabled: false, window_days: 30 });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        http.get("/family-anniversaries/prefs"),
        http.get("/family-anniversaries/upcoming"),
      ]);
      setPrefs(p.data);
      setEvents(u.data.items);
    } catch (e) {
      toast.error("Failed to load family digest");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await http.patch("/family-anniversaries/prefs", { ...prefs, ...patch });
      setPrefs({ enabled: data.anniversary_digest_enabled, window_days: data.anniversary_digest_window_days });
      toast.success("Preferences saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await http.post("/family-anniversaries/send-test");
      toast.success(`Test digest sent to ${data.sent_to} (${data.events} events).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const iconOf = (kind) => ({ birthday: "\u{1F382}", wedding: "\u{1F48D}", remembrance: "\u{1F54A}" }[kind] || "\u{1F4C5}");

  return (
    <div className="mt-6 space-y-5" data-testid="family-digest-tab">
      <section className="wofy-card space-y-4">
        <div className="overline">FAMILY ANNIVERSARY DIGEST</div>
        <h2 className="font-display text-2xl">Never miss a family moment</h2>
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          Opt in to a warm, weekly email of upcoming birthdays, wedding anniversaries, and remembrance
          dates from every tree you belong to. One-tap links to open the tree, RSVP, or light a candle
          for departed loved ones.
        </p>

        <label className="flex items-center gap-3 pt-2">
          <input
            type="checkbox"
            data-testid="fd-enabled"
            checked={prefs.enabled}
            onChange={(e) => save({ enabled: e.target.checked })}
            disabled={saving}
            className="w-4 h-4"
          />
          <span className="text-sm">Email me a weekly family anniversary digest</span>
        </label>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <label className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Look ahead
          </label>
          <select
            data-testid="fd-window"
            className="px-3 py-2 rounded-lg border bg-transparent text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            value={prefs.window_days}
            onChange={(e) => save({ window_days: parseInt(e.target.value, 10) })}
            disabled={saving}
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>

          <button
            onClick={sendTest}
            disabled={sending}
            data-testid="fd-send-test"
            className="wofy-btn-ghost text-sm"
          >
            {sending ? "Sending…" : "Send me a test digest"}
          </button>
        </div>
      </section>

      <WishPrefsSection />

      <section className="wofy-card">
        <div className="overline mb-2">UPCOMING NEXT {prefs.window_days} DAYS</div>
        {loading ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            No anniversaries coming up — a calm season. Enrich your family trees with birthdays and
            wedding dates to fill in this list.
          </p>
        ) : (
          <ul className="divide-y" style={{ borderColor: "var(--wofy-line)" }}>
            {events.slice(0, 20).map((e, i) => (
              <li key={i} className="py-3 flex items-start gap-3" data-testid={`fd-event-${e.kind}-${e.date}`}>
                <span className="text-2xl leading-none">{iconOf(e.kind)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{e.title}</div>
                  <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                    <b>{e.date}</b>
                    {" · "}
                    {e.days_until === 0 ? "today" : `in ${e.days_until} day${e.days_until === 1 ? "" : "s"}`}
                    {" · "}
                    <Link to={`/family/${e.tree_slug || e.tree_id}`} className="underline">{e.tree_name}</Link>
                    {e.memorial_slug && (
                      <>
                        {" · "}
                        <Link to={`/celebrate/${e.memorial_slug}`} className="underline" style={{ color: "#5B4A73" }}>Light a candle</Link>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <CommunityDigestPrefs />
    </div>
  );
}

// --- Wishes & Reminders per-user preferences (occasions system) -------------
function WishPrefsSection() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get("/wishes/preferences").then((r) => setPrefs(r.data)).catch(() => setPrefs(null));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await http.put("/wishes/preferences", patch);
      setPrefs(data);
      toast.success("Preference saved.");
    } catch {
      toast.error("Couldn't save preference.");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return null;
  const Row = ({ field, label, hint, testid }) => (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        className="w-4 h-4 mt-0.5"
        checked={!!prefs[field]}
        disabled={saving}
        onChange={(e) => save({ [field]: e.target.checked })}
        data-testid={testid}
      />
      <span className="text-sm">
        {label}
        <span className="block text-xs" style={{ color: "var(--wofy-text-soft)" }}>{hint}</span>
      </span>
    </label>
  );
  return (
    <section className="wofy-card space-y-4" data-testid="wish-prefs-section">
      <div className="overline">WISHES &amp; REMINDERS</div>
      <h2 className="font-display text-2xl">Occasion alerts, your way</h2>
      <Row
        field="occasion_reminder_emails"
        label="Occasion reminder emails"
        hint="A nudge 3 days before family birthdays, wedding anniversaries and remembrance dates, with a one-tap 'send your wish' link."
        testid="wish-pref-reminders"
      />
      <Row
        field="wishes_digest_enabled"
        label="Weekly wishes digest"
        hint="One email a week summarising new wishes, card signatures and greeting cards across your family trees."
        testid="wish-pref-digest"
      />
      <Row
        field="wish_bell_alerts"
        label="In-app wish alerts"
        hint="Bell notifications when someone writes on your timeline or signs a family card."
        testid="wish-pref-bell"
      />
    </section>
  );
}

function CommunityDigestPrefs() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    http.get("/profile/digest")
      .then((r) => setEnabled(!!r.data?.enabled))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const { data } = await http.put("/profile/digest", { enabled: next });
      setEnabled(!!data.enabled);
      toast.success(next ? "You'll get a daily digest at 8am local." : "Daily digest paused.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const preview = () => {
    // Open the server-rendered preview in a new tab — inherits the session cookie.
    window.open("/api/digest/preview", "_blank", "noopener");
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await http.post("/admin/digest/send-now", { only_user_email: "__self__", force: true }).catch(() => null);
      toast.success("If a copy doesn't arrive in ~1 min, add a city to your profile.");
    } finally { setSending(false); }
  };

  return (
    <section className="wofy-card space-y-4" data-testid="community-digest-prefs">
      <div className="overline">DAILY COMMUNITY DIGEST</div>
      <h2 className="font-display text-2xl">3 things happening in your city — every morning</h2>
      <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        Wake up to a short, hyperlocal email at 8am your time: the freshest Neighborhood Pulse
        posts, upcoming festivals & events, open causes, and headline news — all filtered to the
        city on your profile. One tap unsubscribe any time.
      </p>

      <label className="flex items-center gap-3 pt-2">
        <input
          type="checkbox"
          data-testid="cd-enabled"
          checked={enabled}
          disabled={loading || saving}
          onChange={(e) => toggle(e.target.checked)}
          className="w-4 h-4"
        />
        <span className="text-sm">Email me the daily community digest (8am local time)</span>
      </label>

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button
          type="button"
          onClick={preview}
          className="wofy-btn-secondary inline-flex text-sm"
          data-testid="cd-preview"
        >
          Preview today's digest
        </button>
      </div>
    </section>
  );
}

